import { Injectable } from '@angular/core';
import dayjs from 'dayjs';

import {
  CompareRangePresets,
  CompareRangePresetsKeys,
  dateDisplayFormat,
  dateFormat,
  DateRange,
  DateRangePresets,
} from '../../models/date-service.model';

@Injectable({
  providedIn: 'root',
})
export class DateService {
  public today = dayjs().startOf('day');
  public yesterday = dayjs().subtract(1, 'days').startOf('day');
  public sevenDaysAgo = this.today.clone().subtract(7, 'days');
  public thirtyDaysAgo = this.today.clone().subtract(30, 'days');
  public monthStart = dayjs().startOf('month');
  public yearStart = dayjs().startOf('year');
  public lastMonthStart = this.monthStart.clone().subtract(1, 'months');
  public lastMonthEnd = this.lastMonthStart.clone().endOf('month');
  public thisYearStart = this.yearStart.clone();
  public thisYearEnd = this.today.clone();
  public lastYearStart = this.yearStart.clone().subtract(1, 'years');
  public lastYearEnd = this.lastYearStart.clone().endOf('year');

  public dateFormat = dateFormat;
  public dateDisplayFormat = dateDisplayFormat;

  public _oldestDate = dayjs('2010-01-01', dateFormat);
  public presets: DateRangePresets[] = [
    {
      display: 'Recent (Last 7 Days + Today)',
      id: 'recent',
      range: [this.sevenDaysAgo, this.today],
    },
    {
      display: 'Today',
      id: 'today',
      range: [this.today, this.today],
    },
    {
      display: 'Yesterday',
      id: 'yesterday',
      range: [this.yesterday, this.yesterday],
    },
    {
      display: 'Last 7 Days',
      id: 'last_7_days',
      range: [this.sevenDaysAgo, this.yesterday],
    },
    {
      display: 'Last 30 Days',
      id: 'last_30_days',
      range: [this.thirtyDaysAgo, this.yesterday],
    },
    {
      display: 'This Month',
      id: 'this_month',
      range: [this.monthStart, this.today],
    },
    {
      display: 'Last Month',
      id: 'last_month',
      range: [this.lastMonthStart, this.lastMonthEnd],
    },
    {
      display: 'This Year',
      id: 'this_year',
      range: [this.thisYearStart, this.thisYearEnd],
    },
    {
      display: 'Last Year',
      id: 'last_year',
      range: [this.lastYearStart, this.lastYearEnd],
    },
  ];

  public comparePresets: CompareRangePresets[] = [
    {
      id: 'previous_period',
      display: 'Previous Period',
    },
    {
      id: '3_months_ago',
      display: '3 Months Ago',
    },
    {
      id: '6_months_ago',
      display: '6 Months Ago',
    },
    {
      id: '1_year_ago',
      display: '1 Year Ago',
    },
  ];

  // formats dayjs date with the default date format or passed in format
  public formatDate(
    date: dayjs.Dayjs | string,
    format: string = this.dateFormat
  ): string {
    return dayjs(date).format(format);
  }

  // return the full date range data based on the range id
  public getCurrentRange(range?: DateRange): DateRange {
    if (range == null) {
      return this.getPreset({ id: this.presets[0].id });
    }

    if (range.id === 'all_time') {
      return this.formatCustom(this.allTimeRange());
    }

    if (range.id !== 'custom') {
      return this.getPreset(range);
    }

    if (range.id === 'custom') {
      return this.formatCustom(range);
    }
  }

  // creates CompareTo array of a comparison range from the selected dates and range.
  // dateRange should come from compareRangePresets
  public comparedRange(
    dateRange: CompareRangePresetsKeys,
    relativeToStartDate: dayjs.Dayjs | string,
    relativeToEndDate: dayjs.Dayjs | string
  ): [dayjs.Dayjs, dayjs.Dayjs] | any[] {
    if (dateRange == null) {
      return [];
    }

    relativeToStartDate = dayjs(relativeToStartDate);
    relativeToEndDate = dayjs(relativeToEndDate);
    const dateRangeArray: any[] =
      dateRange === 'previous_period' ? ['1', 'day'] : dateRange.split('_');

    // the span of the date range selected
    const span = relativeToEndDate.clone().diff(relativeToStartDate, 'days');
    // the starting date of the compared to
    const startDateCompare = relativeToStartDate
      .clone()
      .subtract(dateRangeArray[0], dateRangeArray[1]);

    return this.formatComparedRange(dateRange, startDateCompare, span);
  }

  // set the beginning start date of the current user
  public setOldestDate(min_date: string) {
    const newDate = min_date || '2010-01-01';
    this._oldestDate = dayjs(newDate, this.dateFormat);
  }

  // return a range of available periods and a starting default period based on a date range.
  public getTimePeriods(dateRange: DateRange) {
    if (dateRange.id === 'all_time') {
      dateRange = this.formatCustom(this.allTimeRange());
    }
    if (!dateRange.since) {
      dateRange = this.getPreset(dateRange);
    }
    // Adds 1 because the diff calculates the days between whereas we want the days total.
    const range =
      1 + dayjs(dateRange.until).diff(dayjs(dateRange.since), 'days');
    const periods = this.calculatePeriods(range);
    const defaultPeriod = this.calculateDefaultPeriod(range);
    return { defaultPeriod, periods };
  }

  // calculate a range of available periods based on total days
  private calculatePeriods(range: number): string[] {
    let periods = ['hour', 'day', 'week', 'month', 'year'];

    if (range < 3) {
      periods = ['hour'];
    } else if (range < 14) {
      periods = ['day'];
    } else if (range < 91) {
      periods = ['day', 'week'];
    } else if (range < 731) {
      periods = ['week', 'month'];
    } else {
      periods = ['month', 'year'];
    }

    return periods;
  }

  // calculate the default selected period based on total days
  private calculateDefaultPeriod(range: number): string {
    let defaultPeriod = '';

    if (range < 3) {
      defaultPeriod = 'hour';
    } else if (range < 60) {
      defaultPeriod = 'day';
    } else if (range < 731) {
      defaultPeriod = 'week';
    } else if (range < 1095) {
      defaultPeriod = 'month';
    } else {
      defaultPeriod = 'year';
    }
    return defaultPeriod;
  }

  // returns a preset range that matches the passed in id
  private getPreset(range: DateRange): DateRange {
    const selectedPreset = this.presets.find((preset) => {
      return preset.id === range.id;
    });

    let name = '';
    if (selectedPreset?.display === this.presets[0].display) {
      name = 'Recent';
    } else {
      name = selectedPreset.display;
    }

    return {
      display: selectedPreset.display,
      id: selectedPreset.id,
      name,
      since: this.formatDate(selectedPreset.range[0]),
      until: this.formatDate(selectedPreset.range[1]),
    };
  }

  // returns custom formatted range with display formatted for the range
  private formatCustom(range: DateRange): DateRange {
    let display = '';
    const sinceMoment = dayjs(range.since);
    const untilMoment = dayjs(range.until);

    if (this.isSingularDateRange(range)) {
      display = this.formatDate(sinceMoment, this.dateDisplayFormat);
    } else {
      // eslint-disable-next-line max-len
      display = `${this.formatDate(
        sinceMoment,
        this.dateDisplayFormat
      )} - ${this.formatDate(untilMoment, this.dateDisplayFormat)}`;
    }

    return {
      display,
      name: 'Custom',
      id: 'custom',
      since: this.formatDate(sinceMoment),
      until: this.formatDate(untilMoment),
    };
  }

  // create a formatted momentjs compared to date range based on the selected comparedTo date range.
  private formatComparedRange(
    dateRange: CompareRangePresetsKeys,
    startDateCompare: dayjs.Dayjs,
    span: number
  ): [dayjs.Dayjs, dayjs.Dayjs] {
    if (dateRange === 'previous_period') {
      // shift the starting date by one day then subtract the span of the date range for start comparison date
      // end comparison date is one day before the start date.
      return [
        startDateCompare.clone().subtract(span, 'days'),
        startDateCompare,
      ];
    } else {
      // shift the starting date by the range selected
      // shift the end date by the date range span
      return [startDateCompare, startDateCompare.clone().add(span, 'days')];
    }
  }

  private isSingularDateRange(range: DateRange) {
    return (
      range.since === range.until ||
      !range.until ||
      range.until === 'Invalid date'
    );
  }

  private allTimeRange(): DateRange {
    return {
      id: 'custom',
      since: this.formatDate(this._oldestDate),
      until: dayjs().format('YYYY-MM-DD'),
    };
  }
}
